import React, { useEffect, useState } from 'react';
import { Button, Card, CardFooter, Col, Row } from 'reactstrap';
import { useFormik } from 'formik';
import * as Icon from 'react-feather';
import * as Yup from 'yup';
import { useDispatch } from 'react-redux';
import { endOfDay, startOfDay } from 'date-fns';
import { followUpReportGet } from '../../../store/slices/tenantSlices/dailyReportSlice';
import AdminHeader from '../../../components/header/AdminHeader';
import FormDateInput from '../../../components/form/FormDateInput';
import CallexLoader from '../../../components/CallexLoader';
import { userOptionGet } from '../../../store/slices/tenantSlices/agentPerformanceSlice';
import FormSelect from '../../../components/form/FormSelect';
import { useUserRolePermission } from '../../../store/slices/authSlice';
import { checkModulePermission, formatPayloadDate } from '../../../utils/common';
import EditFollowUpModal from './EditFollowUpModal';
import { useTimezone } from '../../../store/slices/commonSlice';
import { toZonedTime } from 'date-fns-tz';

const FollowUpReport = () => {
  const dispatch = useDispatch();
  const userRolePermission = useUserRolePermission();
  const [isFilterOpen, setIsFilterOpen] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [reportData, setReportData] = useState({ data: [], header: [] });
  const [userOptions, setUserOptions] = useState([]);
  const [editRecord, setEditRecord] = useState(null);
  const [isEdit, setIsEdit] = useState(null);
  const timezone = useTimezone();
  const today = toZonedTime(new Date(), timezone);

  const initialValues = {
    userId: '',
    from_date: startOfDay(today),
    to_date: endOfDay(today)
  };

  const validationSchema = Yup.object({
    from_date: Yup.date().required('Start date is required'),
    to_date: Yup.date().required('End date is required'),
    userId: Yup.string().required('user is required')
  });

  const onSubmit = async () => {
    setIsLoading(true);
    setIsFilterOpen(false);
    try {
      const formattedStartDate = formatPayloadDate(values.from_date);
      const formattedEndDate = formatPayloadDate(values.to_date);
      const payload = { ...values };
      payload['from_date'] = formattedStartDate;
      payload['to_date'] = formattedEndDate;
      payload['userId'] = values?.userId || undefined;

      const response = await dispatch(followUpReportGet(payload)).unwrap();
      if (response) {
        setReportData({ data: response?.data || [], header: response?.header || [] });
      }
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.log('error', error);
    }
  };

  const getUserOption = async () => {
    try {
      const response = await dispatch(userOptionGet()).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.Username || '-',
          value: val.id
        }));
        setUserOptions(options);
      } else {
        setUserOptions([]);
      }
    } catch (error) {
      setUserOptions([]);
      console.log('error', error);
    }
  };

  useEffect(() => {
    getUserOption();
  }, []);

  const { values, handleBlur, handleChange, setFieldValue, handleSubmit, touched, errors } =
    useFormik({
      initialValues,
      validationSchema,
      onSubmit
    });

  return (
    <div>
      <AdminHeader
        title="Follow Up"
        isSearch
        onSearchClick={() => setIsFilterOpen(!isFilterOpen)}
      />
      {isFilterOpen && (
        <Card className="mb-3 p-3">
          <form onSubmit={handleSubmit}>
            <Row>
              <Col lg="3" md="3" className="flex-row">
                <FormDateInput
                  name="from_date"
                  dateFormat="yyyy-MM-dd HH:mm:ss"
                  label="From Date"
                  showTimeSelect={true}
                  maxDate={today}
                  value={values.from_date}
                  onChange={(e) => setFieldValue('from_date', e.target.value)}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="3" md="3" className="flex-row">
                <FormDateInput
                  name="to_date"
                  label="To Date"
                  dateFormat="yyyy-MM-dd HH:mm:ss"
                  showTimeSelect={true}
                  maxDate={today}
                  value={values.to_date}
                  onChange={(e) => setFieldValue('to_date', e.target.value)}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="3" md="3" className="flex-row">
                <FormSelect
                  label="User"
                  placeholder="Select User"
                  value={values.userId}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  name="userId"
                  options={userOptions}
                  touched={touched}
                  errors={errors}
                />
              </Col>
            </Row>
            <CardFooter className="p-0 bg-white text-end">
              <Button color="primary" className="px-4" type="submit" disabled={isLoading}>
                Search
              </Button>
            </CardFooter>
          </form>
        </Card>
      )}

      {isLoading ? (
        <Card
          className="p-0 d-flex justify-content-center align-items-center m-0"
          style={{ height: isFilterOpen ? 'calc(100vh - 330px)' : 'calc(100vh - 170px)' }}>
          <CallexLoader />
        </Card>
      ) : reportData?.data?.length > 0 ? (
        <Card className="p-0 position-relative">
          <div
            className="daily-report-table"
            style={{ height: isFilterOpen ? 'calc(100vh - 330px)' : 'calc(100vh - 170px)' }}>
            <table className="report-list-table">
              <thead>
                <tr>
                  {reportData?.header?.map((val, index) => {
                    return <th key={index}>{val.label}</th>;
                  })}
                  <th className="text-center" style={{ width: '90px' }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {reportData?.data?.map((val, index) => {
                  return (
                    <tr
                      key={index}
                      style={{
                        background:
                          val?.follow_up_status === 'done'
                            ? '#2cabe333'
                            : val?.follow_up_status === 'pending'
                              ? '#ff505033'
                              : '#ffb22b33'
                      }}>
                      {reportData?.header?.map((value, idx) => {
                        if (value?.name === 'follow_up_status') {
                          return (
                            <td key={idx} style={{ textTransform: 'capitalize' }}>
                              {val[value?.name] || '-'}
                            </td>
                          );
                        }
                        return <td key={idx}>{val[value?.name] || '-'}</td>;
                      })}
                      <td>
                        <div className="d-flex justify-content-around align-items-center">
                          <div
                            className={`cursor-pointer text-info ${
                              !checkModulePermission('is_edit', 'Follow Up', userRolePermission)
                                ? 'callex-disabled'
                                : ''
                            }`}>
                            <Icon.Edit
                              size={16}
                              onClick={() => {
                                if (
                                  checkModulePermission('is_edit', 'Follow Up', userRolePermission)
                                ) {
                                  setEditRecord(val);
                                  setIsEdit(true);
                                }
                              }}
                            />
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      ) : (
        <Card
          className="p-0 d-flex justify-content-center align-items-center no-data m-0"
          style={{ height: isFilterOpen ? 'calc(100vh - 330px)' : 'calc(100vh - 170px)' }}>
          No data available
        </Card>
      )}
      {isEdit && (
        <EditFollowUpModal
          isOpen={isEdit}
          editRecord={editRecord}
          userOptions={userOptions}
          onCloseClick={(apiCall = false) => {
            setEditRecord(null);
            setIsEdit(false);
            if (apiCall) {
              onSubmit();
            }
          }}
        />
      )}
    </div>
  );
};

export default FollowUpReport;
